/**
 * @fileoverview TipTap Ordered List Style Extension
 *
 * Extends the StarterKit `OrderedList` node with a `listStyle` attribute so
 * users can switch the marker style on the current `<ol>` between:
 *   - decimal              → "1. 2. 3."
 *   - lower-alpha-paren    → "a) b) c)"
 *   - upper-alpha-paren    → "A) B) C)"
 *   - lower-roman-paren    → "i) ii) iii)"
 *   - upper-roman-paren    → "I) II) III)"
 *
 * The trailing ")" is provided by CSS `@counter-style` rules in
 * `editor-styles.css` (editor preview), `index.css` (`.prose-letter`
 * rendered preview), and `buildPrintCss` in `exportHtmlDocument.ts`
 * (PDF export). Word `altChunk` import does not understand the custom
 * counter style names — it falls back to its native numbering, which is
 * the cleanest available degradation.
 *
 * Round-trip storage: the attribute serialises as
 * `<ol style="list-style-type: <value>" data-list-style="<value>">`. The
 * `data-` attribute is the source of truth on parse so a CSS rewriter
 * can drop the inline style without losing the choice.
 */

import { OrderedList } from "@tiptap/extension-list/ordered-list";

export type OrderedListStyle =
  | "decimal"
  | "lower-alpha-paren"
  | "upper-alpha-paren"
  | "lower-roman-paren"
  | "upper-roman-paren";

const VALID_STYLES = new Set<OrderedListStyle>([
  "decimal",
  "lower-alpha-paren",
  "upper-alpha-paren",
  "lower-roman-paren",
  "upper-roman-paren",
]);

/**
 * Map legacy / standard CSS values onto our paren-suffix variants.
 * Lets templates authored elsewhere (or pasted from Word) come in with the
 * familiar marker style instead of silently flipping to "1.".
 */
function normaliseListStyle(
  value: string | null | undefined,
): OrderedListStyle | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (VALID_STYLES.has(v as OrderedListStyle)) return v as OrderedListStyle;
  if (v === "a" || v === "lower-alpha" || v === "lower-latin")
    return "lower-alpha-paren";
  if (v === "upper-alpha" || v === "upper-latin") return "upper-alpha-paren";
  if (v === "i" || v === "lower-roman") return "lower-roman-paren";
  if (v === "upper-roman") return "upper-roman-paren";
  if (v === "1" || v === "decimal") return "decimal";
  return null;
}

export const StyledOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      listStyle: {
        default: null as OrderedListStyle | null,
        parseHTML: (element: HTMLElement): OrderedListStyle | null => {
          const dataAttr = element.getAttribute("data-list-style");
          const fromData = normaliseListStyle(dataAttr);
          if (fromData) return fromData;

          const styleAttr = element.getAttribute("style") ?? "";
          const match = styleAttr.match(/list-style-type:\s*([a-z0-9-]+)/i);
          const fromStyle = normaliseListStyle(match?.[1]);
          if (fromStyle) return fromStyle;

          // HTML5 native `<ol type="a|A|i|I|1">`.
          const typeAttr = element.getAttribute("type");
          if (typeAttr === "a") return "lower-alpha-paren";
          if (typeAttr === "A") return "upper-alpha-paren";
          if (typeAttr === "i") return "lower-roman-paren";
          if (typeAttr === "I") return "upper-roman-paren";
          if (typeAttr === "1") return "decimal";

          return null;
        },
        renderHTML: (attributes: { listStyle?: OrderedListStyle | null }) => {
          const style = attributes.listStyle;
          if (!style || style === "decimal") return {};
          return {
            style: `list-style-type: ${style}`,
            "data-list-style": style,
          };
        },
      },
    };
  },
});
